/**
 * EDL Generator (CMX 3600)
 * 
 * Exports the current timeline state to an Edit Decision List (EDL)
 * compatible with DaVinci Resolve, Premiere Pro, Avid, etc.
 */

import { SegmentWithUI } from '../types/timeline';

interface EDLOptions {
    title: string;
    fps: number;
}

/**
 * Convert frame count to SMPTE Timecode (HH:MM:SS:FF)
 */
const framesToTimecode = (frames: number, fps: number): string => {
    // Handle non-drop frame only for simplicity (standard for standard web FPS like 24, 25, 30)
    const h = Math.floor(frames / (fps * 3600));
    const m = Math.floor((frames % (fps * 3600)) / (fps * 60));
    const s = Math.floor((frames % (fps * 60)) / fps);
    const f = Math.floor(frames % fps);

    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}:${pad(f)}`;
};

/**
 * Generate CMX 3600 EDL string
 */
export const generateEDL = (segments: SegmentWithUI[], options: EDLOptions): string => {
    const { title, fps } = options;
    const lines: string[] = [];

    // Header
    lines.push(`TITLE: ${title.toUpperCase().substring(0, 70)}`);
    lines.push('FCM: NON-DROP FRAME');
    lines.push('');

    // Sort segments by timeline position
    const sortedSegments = [...segments].sort((a, b) => a.inSec - b.inSec);

    sortedSegments.forEach((seg, index) => {
        // Event Number (001, 002...)
        const eventNum = (index + 1).toString().padStart(3, '0');

        // Tape Name (Reel)
        // Use "AX" (Auxiliary) for generated media without specific reel
        // Or "GENAI" to group them.
        const reel = 'GENAI';

        // Track Type (V=Video, A=Audio, AA=Stereo)
        // Assuming V only for now as per current data model
        const trackType = 'V';

        // Edit Type (C=Cut, D=Dissolve, W=Wipe)
        const editType = 'C';

        // Timecodes (Source In, Source Out, Record In, Record Out)
        // Record TC (Timeline)
        // Note: TimelineService uses float seconds. We must convert to frames.
        const recInFrame = Math.round(seg.inSec * fps);
        const recOutFrame = Math.round(seg.outSec * fps);

        // Source TC
        // If segment has explicit source frames (from Rush import), use them.
        // Otherwise, assume full clip usage from 0.
        let srcInFrame = 0;
        let srcOutFrame = 0;

        if (seg.sourceStartFrame !== undefined) {
            srcInFrame = seg.sourceStartFrame;
            srcOutFrame = srcInFrame + (recOutFrame - recInFrame);
        } else {
            // Fallback for generated content: Source matches duration used
            srcInFrame = 0;
            srcOutFrame = recOutFrame - recInFrame;
        }

        const srcInTC = framesToTimecode(srcInFrame, fps);
        const srcOutTC = framesToTimecode(srcOutFrame, fps);
        const recInTC = framesToTimecode(recInFrame, fps);
        const recOutTC = framesToTimecode(recOutFrame, fps);

        // Standard CMX 3600 Line
        // 001  GENAI    V     C        00:00:00:00 00:00:04:00 01:00:00:00 01:00:04:00
        // Adjust Record In to start at 01:00:00:00 (standard hour 1 start)
        // or keep relative to 00:00:00:00. Let's start at 01:00:00:00 for "Pro" feel.
        const hourOneOffset = fps * 3600;
        const recInTC_Hour1 = framesToTimecode(recInFrame + hourOneOffset, fps);
        const recOutTC_Hour1 = framesToTimecode(recOutFrame + hourOneOffset, fps);

        lines.push(`${eventNum}  ${reel.padEnd(8)} ${trackType}     ${editType}        ${srcInTC} ${srcOutTC} ${recInTC_Hour1} ${recOutTC_Hour1}`);

        // Comments (Clip Name)
        // * FROM CLIP NAME: xxxx
        // Try to find the filename or label
        let clipName = seg.label || `SEGMENT_${eventNum}`;
        const activeRev = seg.activeRevision;
        if (activeRev) {
            // Try to deduce a filename
            if (activeRev.outputAsset?.url) {
                const parts = activeRev.outputAsset.url.split('/');
                const file = parts[parts.length - 1];
                clipName = file.split('?')[0]; // Remove query params if any
            }
        }

        lines.push(`* FROM CLIP NAME: ${clipName}`);

        // Add prompt as comment for context
        if (activeRev?.promptJson?.rootPrompt) {
            lines.push(`* COMMENT: PROMPT: ${activeRev.promptJson.rootPrompt.substring(0, 60)}...`);
        }

        lines.push(''); // Spacer
    });

    return lines.join('\n');
};
